import { defineStore } from 'pinia'
import { signin } from '@/api/lists.js'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { db } from '@/firebaseConfig'
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { USERS_DB } from '@/constants/constants.js'

export const useUserStore = defineStore('users', {
  state: () => ({
    user: null,
    useremail: null,
    loggedin: false,
    authListenerUnsubscribe: null,
    status: null,
    error: null
  }),

  getters: {
    username: (state) => state.user?.alias,
    userid: (state) => state.user?.user_id,
    admin: (state) => state.user?.permissions?.admin,
    favoriteplaces: (state) => state.user?.favoriteplaces || [],
    favoritegames: (state) => state.user?.favoritegames || [],
    /** Aliases of users who are allowed to add this user to tournaments (track players by name). */
    allowedTournamentEnterers: (state) => state.user?.allowedTournamentEnterers ?? [],
    /** True if user has poker tracker permission (can use session list and allow others to add them to tournaments). */
    hasPokerPermission: (state) => state.user?.permissions?.poker === 'approved',
    placeGroups: (state) => state.user?.placeGroups ?? [],
  },

  actions: {
    setUser(userData) {
      if (userData) {
        this.user = userData;
        this.useremail = userData.email;
      } else {
        this.user = null;
        this.useremail = null;
      }
      this.loggedin = !!userData;
      this.status = 'success';
      this.error = null;
    },

    async login(email, password) {
      this.status = 'loading';
      try {
        const cred = await signin({ email, password });
        const userRef = collection(db, USERS_DB);
        const q = query(userRef, where("user_id", "==", cred.user.uid));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const userDoc = snapshot.docs[0];
          const userData = userDoc.data();
          userData.id = userDoc.id; // Firestore document id (for updates)
          userData.email = cred.user.email;
          userData.user_id = cred.user.uid;
          this.setUser(userData);
          // Record last login — fire and forget, don't block the login flow
          updateDoc(doc(db, USERS_DB, userDoc.id), { lastLogin: serverTimestamp() }).catch(() => {});
        }
        return cred;
      } catch (err) {
        this.status = 'error';
        this.error = err;
        throw err;
      }
    },

    /** Update the user's alias. Persists to Firestore and updates in-memory state. */
    async updateAlias(newAlias) {
      if (!this.user) return;
      const trimmed = newAlias.trim();
      const docId = this.user.id ?? this.user.user_id;
      if (!docId) return;
      await updateDoc(doc(db, USERS_DB, docId), {
        alias: trimmed,
        aliasLower: trimmed.toLowerCase(),
      });
      this.user.alias = trimmed;
      this.user.aliasLower = trimmed.toLowerCase();
    },

    /** Update the user's place groups. Persists to Firestore and updates in-memory state. */
    async updatePlaceGroups(groups) {
      if (!this.user) return;
      const docId = this.user.id ?? this.user.user_id;
      if (!docId) return;
      await updateDoc(doc(db, USERS_DB, docId), { placeGroups: groups });
      this.user.placeGroups = groups;
    },

    /** Update the list of aliases who can add this user to tournaments. Persists to Firestore. */
    async updateAllowedTournamentEnterers(aliases) {
      if (!this.user || !Array.isArray(aliases)) return;
      // Use Firestore document id (id from query); fallback to user_id for signup-created docs
      const docId = this.user.id ?? this.user.user_id;
      if (!docId) return;
      const userRef = doc(db, USERS_DB, docId);
      await updateDoc(userRef, { allowedTournamentEnterers: aliases });
      this.user.allowedTournamentEnterers = aliases;
    },

    async checkLogin() {
      if (this.authListenerUnsubscribe) {
        return this.loggedin;
      }

      const auth = getAuth();
      return new Promise(resolve => {
        this.authListenerUnsubscribe = onAuthStateChanged(auth, async user => {
          if (!user) {
            this.setUser(null);
            return resolve(false);
          }

          // Only fetch if user data is not already in store
          if (!this.user || this.user.user_id !== user.uid) {
            try {
              const userRef = collection(db, USERS_DB);
              const q = query(userRef, where("user_id", "==", user.uid));
              const snapshot = await getDocs(q);
              if (!snapshot.empty) {
                const userDoc = snapshot.docs[0];
                const userData = userDoc.data();
                userData.id = userDoc.id; // Firestore document id (for updates)
                userData.email = user.email;
                userData.user_id = user.uid;
                this.setUser(userData);
              } else {
                this.setUser(null); // Auth user exists, but no profile in DB
              }
            } catch (error) {
              console.error("Error fetching user profile in checkLogin:", error);
              this.setUser(null);
            }
          }
          resolve(this.loggedin);
        });
      });
    },
  }
});